import { useState } from 'react'
import { ShieldAlert, FileText } from 'lucide-react'
import { getNdaSigningUrl, requestDisclosureAdvance } from '../../services/api'
import type { DisclosureAgreement } from '../../types'

interface Props {
  agreements: DisclosureAgreement[]
}

export default function ConsentRequestBanner({ agreements }: Props) {
  const [loadingNda, setLoadingNda] = useState<string | null>(null)

  // An agreement needs MY attention if either (a) a mutual NDA is pending — both
  // parties must sign before Level 4 — or (b) my partner has consented to a higher
  // level and is waiting on me. The NDA case is independent of consent levels: at
  // the L3→L4 gate both parties sit at the same level with nda_status='pending', so
  // a consent-mismatch-only filter would hide the Sign NDA button entirely (the
  // gate had no in-app way to sign).
  const needsAction = agreements.filter(
    (a) =>
      a.nda_status === 'pending' ||
      (a.my_consented_level !== undefined &&
        a.counterparty_consented_level !== undefined &&
        a.my_consented_level < a.counterparty_consented_level),
  )

  if (needsAction.length === 0) return null

  const handleSignNda = async (agreement: DisclosureAgreement) => {
    setLoadingNda(agreement.agreement_id)
    // Open synchronously BEFORE any await — browsers block window.open after async boundaries.
    // noopener/noreferrer cause window.open to return null in Chrome, preventing the
    // location.href redirect below. Open without those flags, then null opener after
    // redirect so the DocuSign page can't reference back to this app.
    const w = window.open('', '_blank')
    try {
      // Provision (or re-provision) the envelope before fetching the signing URL.
      // nda_status='pending' with no nda_envelope_id (fixture / partial write) causes
      // nda-url to 400 if we skip this. Backend handles re-requests gracefully.
      await requestDisclosureAdvance({ agreementId: agreement.agreement_id })
      const { url } = await getNdaSigningUrl(agreement.agreement_id)
      if (w && !w.closed) {
        w.opener = null  // sever opener reference before handing off to DocuSign
        w.location.href = url
      }
      // If popup was blocked (w is null) or user closed the tab, the button stays
      // enabled so a second click mints a fresh short-lived DocuSign token rather
      // than reusing a cached URL that will have TTL-expired.
    } catch {
      // Keep the blank window open — it counts as "Sign NDA opened" per test A2.
      // If popup was blocked (w is null), nothing to close anyway.
    } finally {
      setLoadingNda(null)
    }
  }

  return (
    <div className="space-y-2 mb-4">
      {needsAction.map((agreement) => {
        const ndaPending = agreement.nda_status === 'pending'
        // The already-signed party must NOT be told to sign again — show a waiting state.
        const iHaveSignedNda = ndaPending && agreement.my_nda_signed === true
        return (
          <div
            key={agreement.agreement_id}
            className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
          >
            <ShieldAlert size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                {!ndaPending
                  ? 'Disclosure advance pending your consent'
                  : iHaveSignedNda
                    ? 'Mutual NDA signed — awaiting your counterparty'
                    : 'Mutual NDA awaiting your signature'}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                {!ndaPending
                  ? `Your partner has consented to level ${agreement.counterparty_consented_level}. You are at level ${agreement.my_consented_level}.`
                  : iHaveSignedNda
                    ? 'You have signed. Level 4 (identity reveal) unlocks once your counterparty signs too.'
                    : 'Both parties must sign the mutual NDA to unlock Level 4 (identity reveal).'}
              </p>
            </div>
            {ndaPending && !iHaveSignedNda && (
              <button
                onClick={() => handleSignNda(agreement)}
                disabled={loadingNda === agreement.agreement_id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 shrink-0"
              >
                <FileText size={12} />
                {loadingNda === agreement.agreement_id ? 'Loading…' : 'Sign NDA'}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
